"use client";

import React from "react";
import { SlidersHorizontal, OctagonAlert, Eye, FileText, TriangleAlert as AlertTriangle } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
interface ProtocolCard {
  id: string;
  code: string;
  title: string;
  description: string;
  icon: React.ElementType;
}
const PROTOCOL_ITEMS: ProtocolCard[] = [{
  id: "controls",
  code: "CTRL-01",
  title: "Know the Controls",
  description: "Understand how the supplied controller operates.",
  icon: SlidersHorizontal
}, {
  id: "stop-procedure",
  code: "SAFE-02",
  title: "Know the Stop Procedure",
  description: "Identify the normal and emergency stopping controls.",
  icon: OctagonAlert
}, {
  id: "operating-area",
  code: "SURV-03",
  title: "Watch the Operating Area",
  description: "Maintain a clear view of the rider and surrounding area.",
  icon: Eye
}, {
  id: "product-instructions",
  code: "DOCS-04",
  title: "Follow Product Instructions",
  description: "Use the documentation supplied for the exact machine.",
  icon: FileText
}];
export default function Preparation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="operatorpreparation" data-nav="light" className="w-full bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Datum Hairline Header Boundary */}
        <div className="border border-border bg-card/60 p-6 sm:p-8 lg:p-10 mb-8 sm:mb-12">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <span className="inline-block size-1.5 bg-primary" />
                OPERATIONAL SPECIFICATION // SAFETY PROTOCOL
              </span>
              <Badge variant="outline" className="font-mono text-2xs uppercase tracking-widest rounded-none border-border bg-muted/40 text-muted-foreground">
                EQUIPMENT STANDARD
              </Badge>
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground">
              Operator Preparation Matters
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 pt-2">
              <p className="font-sans text-base sm:text-lg leading-relaxed text-foreground">
                The person controlling the mechanical bull should understand the controls and the operating instructions for that specific machine before riders use it.
              </p>
              <p className="font-sans text-sm sm:text-base leading-relaxed text-muted-foreground">
                Different systems may offer different control arrangements, operating modes and emergency functions. Therefore, operators should not assume that experience with one mechanical bull automatically translates to another model.
              </p>
            </div>
          </div>
        </div>

        {/* Spec-Plate Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {PROTOCOL_ITEMS.map((item, index) => {
          const Icon = item.icon;
          return <motion.article key={item.id} data-index={index} variants={cardVariants} className="group relative flex flex-col justify-between border border-border bg-card p-6 rounded-none transition-colors duration-200 hover:border-primary">
                <div>
                  <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                    <div className="flex size-10 items-center justify-center border border-border bg-muted/30 text-primary group-hover:border-primary transition-colors duration-200">
                      <Icon className="size-5" />
                    </div>
                    <span className="font-mono text-2xs uppercase tracking-widest text-muted-foreground">
                      {item.code}
                    </span>
                  </div>

                  <h3 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight text-card-foreground mb-3">
                    {item.title}
                  </h3>

                  <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>

                <div className="pt-6 mt-6 border-t border-border/60 flex items-center justify-between text-2xs font-mono text-muted-foreground/80 uppercase">
                  <span>SEC-{index + 1}</span>
                  <span className="inline-block size-1 bg-border group-hover:bg-primary transition-colors duration-200" />
                </div>
              </motion.article>;
        })}
        </motion.div>

        {/* Footer Technical Note */}
        <div className="mt-8 border border-border/80 bg-muted/20 px-4 py-3 flex items-center gap-3">
          <AlertTriangle className="size-4 shrink-0 text-primary" />
          <p className="font-mono text-xs text-muted-foreground">
            NOTICE: Technical guidelines are provided for equipment operation awareness. Always consult the specific manufacturer technical manual supplied with your unit.
          </p>
        </div>
      </div>
    </section>;
}